import { useRef, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import axios from '../../api/axios';
import { Alert, CircularProgress } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import 'bootstrap/dist/css/bootstrap.min.css';
import AuthPageLayout from './AuthPageLayout';

const REGISTER_URL = '/Registration/Register';

const Register = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const userRef = useRef();
    const errRef = useRef();

    const [formData, setFormData] = useState({
        username: '',
        email: '',
        fullName: '',
        password: '',
        confirmPassword: ''
    });
    const [errMsg, setErrMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Password validation states
    const [validPassword, setValidPassword] = useState(false);
    const [passwordFocus, setPasswordFocus] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);
    const handleMouseDownPassword = (event) => event.preventDefault();

    // Password requirements regex - requires lowercase, uppercase, digit, and any non-alphanumeric character
    const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/;

    useEffect(() => {
        if (userRef.current) {
            userRef.current.focus();
        }
    }, []);

    useEffect(() => {
        setValidPassword(PWD_REGEX.test(formData.password));
    }, [formData.password]);

    useEffect(() => {
        setErrMsg('');
    }, [formData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate password match
        if (formData.password !== formData.confirmPassword) {
            setErrMsg(t('passwordsMustMatch'));
            return;
        }

        // Validate password strength
        if (!PWD_REGEX.test(formData.password)) {
            setErrMsg(t('registerPasswordRequirements'));
            return;
        }

        setIsLoading(true);
        setErrMsg('');

        try {
            const response = await axios.post(
                REGISTER_URL,
                formData,
                {
                    headers: { 'Content-Type': 'application/json' }
                }
            );

            if (response.data.success) {
                setIsSubmitted(true);
                setSuccessMsg(t('registerSuccess'));
            }
        } catch (err) {
            if (!err?.response) {
                setErrMsg(t('errorNoServerResponse'));
            } else if (err.response?.status === 400) {
                setErrMsg(err.response?.data?.message || t('registerError'));
            } else {
                setErrMsg(t('registerError'));
            }
            if (errRef.current) {
                errRef.current.focus();
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (isSubmitted) {
        return (
            <AuthPageLayout>
                <div
                    className="card shadow"
                    style={{ width: '100%', maxWidth: '450px', border: 'none', borderRadius: '0.5rem' }}
                >
                    <div className="card-body text-center p-4">
                        <Link to="/Home">
                            <img
                                src="/assets/img/OnlineCADCOM_FullLogo.png"
                                alt="Online-CADCOM"
                                style={{ height: '50px', marginBottom: '16px' }}
                            />
                        </Link>
                        <CheckCircleIcon sx={{ fontSize: 60, color: '#28a745', mb: 2 }} />
                        <h3 style={{ color: '#333' }}>{t('registerSuccessTitle')}</h3>
                        <p className="text-muted mb-4">{successMsg}</p>
                        <p className="text-muted mb-4">{t('registerPendingMessage')}</p>
                        <Link
                            to="/login"
                            className="btn btn-lg w-100"
                            style={{ backgroundColor: '#112d4e', color: '#fff', border: 'none' }}
                        >
                            {t('backToLogin')}
                        </Link>
                    </div>
                </div>
            </AuthPageLayout>
        );
    }

    return (
        <AuthPageLayout>
            <div
                className="card shadow"
                style={{ width: '100%', maxWidth: '450px', border: 'none', borderRadius: '0.5rem' }}
            >
                <div className="card-body">
                    <div className="text-center mb-4">
                        <Link to="/Home">
                            <img
                                src="/assets/img/OnlineCADCOM_FullLogo.png"
                                alt="Online-CADCOM"
                                style={{ height: '60px' }}
                            />
                        </Link>
                    </div>

                    {errMsg && (
                        <Alert
                            ref={errRef}
                            variant="filled"
                            severity="error"
                            className="mb-3"
                            tabIndex={-1}
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => setErrMsg('')}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            }
                        >
                            {errMsg}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="form-floating mb-3">
                            <input
                                type="text"
                                className="form-control"
                                id="fullName"
                                name="fullName"
                                placeholder={t("registerFullName")}
                                ref={userRef}
                                onChange={handleChange}
                                value={formData.fullName}
                                required
                                minLength={2}
                                maxLength={200}
                            />
                            <label htmlFor="fullName">{t("registerFullName")}</label>
                        </div>

                        <div className="form-floating mb-3">
                            <input
                                type="text"
                                className="form-control"
                                id="username"
                                name="username"
                                placeholder={t("registerUsername")}
                                onChange={handleChange}
                                value={formData.username}
                                required
                                minLength={3}
                                maxLength={100}
                                autoComplete="username"
                            />
                            <label htmlFor="username">{t("registerUsername")}</label>
                        </div>

                        <div className="form-floating mb-3">
                            <input
                                type="email"
                                className="form-control"
                                id="email"
                                name="email"
                                placeholder={t("registerEmail")}
                                onChange={handleChange}
                                value={formData.email}
                                required
                                maxLength={256}
                                autoComplete="email"
                            />
                            <label htmlFor="email">{t("registerEmail")}</label>
                        </div>

                        <div className="form-floating mb-3 position-relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                className="form-control"
                                id="password"
                                name="password"
                                placeholder={t("registerPassword")}
                                onChange={handleChange}
                                value={formData.password}
                                required
                                minLength={8}
                                maxLength={100}
                                autoComplete="new-password"
                                onFocus={() => setPasswordFocus(true)}
                                onBlur={() => setPasswordFocus(false)}
                                style={{ paddingRight: '45px' }}
                            />
                            <label htmlFor="password">{t("registerPassword")}</label>
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                                size="small"
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    zIndex: 2
                                }}
                            >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </div>

                        {passwordFocus && !validPassword && (
                            <div className="alert alert-info py-2 small mb-3">
                                {t("registerPasswordHint")}
                            </div>
                        )}

                        <div className="form-floating mb-3 position-relative">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                className="form-control"
                                id="confirmPassword"
                                name="confirmPassword"
                                placeholder={t("registerConfirmPassword")}
                                onChange={handleChange}
                                value={formData.confirmPassword}
                                required
                                autoComplete="new-password"
                                style={{ paddingRight: '45px' }}
                            />
                            <label htmlFor="confirmPassword">{t("registerConfirmPassword")}</label>
                            <IconButton
                                aria-label="toggle confirm password visibility"
                                onClick={handleClickShowConfirmPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                                size="small"
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    zIndex: 2
                                }}
                            >
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </div>

                        <button
                            className="btn btn-lg w-100 mb-3"
                            type="submit"
                            disabled={isLoading}
                            style={{ backgroundColor: '#112d4e', color: '#fff', border: 'none' }}
                        >
                            {isLoading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                t("registerSubmitButton")
                            )}
                        </button>
                    </form>

                    <div className="text-center">
                        <p className="mb-0">
                            {t("registerAlreadyHaveAccount")}{' '}
                            <Link to="/login" style={{ color: '#112d4e', fontWeight: 'bold' }}>
                                {t("registerLoginLink")}
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </AuthPageLayout>
    );
};

export default Register;
